import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Receipt, CurrencyInr, Users, FolderOpen, Eye, ChartLine } from "@phosphor-icons/react";

/**
 * Tier 3 dashboard — Department Head view.
 * Renders department-specific tiles for Finance · HR · Consulting.
 */
export default function DeptHeadDashboard() {
  const { user } = useAuth();
  const dept = (user?.department || "").toLowerCase();

  if (dept === "finance") return <FinanceHeadDashboard user={user} />;
  if (dept === "hr") return <HRHeadDashboard user={user} />;
  if (dept === "consulting") return <ConsultingHeadDashboard user={user} />;
  return <GenericHeadDashboard user={user} />;
}

// ------------------ Finance ------------------
function FinanceHeadDashboard({ user }) {
  const [data, setData] = useState(null);
  const [aging, setAging] = useState(null);
  useEffect(() => { (async () => {
    try {
      const [tax, invs, claims, ag] = await Promise.all([
        api.get(`/finance/tax-dashboard?year=${new Date().getFullYear()}`).catch(() => ({ data: null })),
        api.get("/invoices/client").catch(() => ({ data: [] })),
        api.get("/expenses/claims").catch(() => ({ data: [] })),
        api.get("/analytics/receivables-aging").catch(() => ({ data: null })),
      ]);
      setData({
        tax: tax.data,
        invoiceQueue: (invs.data || []).filter(i => i.status !== "paid").slice(0, 10),
        pendingClaims: (claims.data || []).filter(c => c.status === "submitted").slice(0, 10),
      });
      setAging(ag.data);
    } catch (e) { setData({}); }
  })(); }, []);

  return (
    <div className="space-y-6" data-testid="dept-head-dashboard-finance">
      <Header user={user} title="Finance · Department Dashboard" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Tile label="Open invoices" value={data?.invoiceQueue?.length ?? "—"} sub="Awaiting payment" testid="dh-fin-open-invoices" />
        <Tile label="Claims to approve" value={data?.pendingClaims?.length ?? "—"} sub="Submitted, awaiting approval" testid="dh-fin-pending-claims" />
        <Tile label={`GST 18% (${new Date().getFullYear()})`} value={data?.tax?.gst_collected_inr != null ? fmtMoney(data.tax.gst_collected_inr, "INR") : "—"} sub="Year-to-date collected" testid="dh-fin-gst-due" />
      </div>
      {aging && <ReceivablesAgingTile aging={aging} />}
      <Pair
        leftTitle="Invoice queue" leftLink="/invoices" leftTestid="dh-fin-invoice-list"
        leftItems={(data?.invoiceQueue || []).map(i => ({ key: i.invoice_id, text: `${i.invoice_number} · ${i.currency} · ${i.status}` }))}
        rightTitle="Expense claims pending" rightLink="/payables" rightTestid="dh-fin-claims-list"
        rightItems={(data?.pendingClaims || []).map(c => ({ key: c.claim_id, text: `${c.user_name} · ₹${c.total_inr}` }))}
      />
    </div>
  );
}

// ------------------ HR ------------------
function HRHeadDashboard({ user }) {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => {
    try {
      const [users, leaves, payrolls] = await Promise.all([
        api.get("/users").catch(() => ({ data: [] })),
        api.get("/hr/leave-requests?status=pending").catch(() => ({ data: [] })),
        api.get("/hr/payroll-runs").catch(() => ({ data: [] })),
      ]);
      setData({
        activeCount: (users.data || []).filter(u => !u.is_distribution_alias && u.active !== false).length,
        leavesPending: leaves.data || [],
        nextPayroll: (payrolls.data || []).find(p => p.status === "draft") || (payrolls.data || [])[0],
      });
    } catch (e) { setData({}); }
  })(); }, []);

  return (
    <div className="space-y-6" data-testid="dept-head-dashboard-hr">
      <Header user={user} title="HR · Department Dashboard" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Tile label="Active employees" value={data?.activeCount ?? "—"} testid="dh-hr-active" />
        <Tile label="Leave requests pending" value={data?.leavesPending?.length ?? "—"} sub="Awaiting your approval" testid="dh-hr-leaves" />
        <Tile label="Next payroll run" value={data?.nextPayroll?.period || "—"} sub={data?.nextPayroll?.status || "—"} testid="dh-hr-payroll" />
      </div>
      <div className="card-flat">
        <div className="px-5 py-3 border-b border-[var(--span-border)] flex items-center justify-between">
          <div className="label-eyebrow">Leave requests pending</div>
          <Link to="/settings/team" className="text-[11px] font-mono text-[var(--span-text-2)] uppercase tracking-[0.12em] hover:text-[var(--span-text)]">Team →</Link>
        </div>
        {(!data?.leavesPending || data.leavesPending.length === 0) ? (
          <div className="px-5 py-6 text-center text-[12px] text-[var(--span-text-2)]">No pending leave requests.</div>
        ) : (
          <ul className="divide-y divide-[var(--span-border)]">
            {data.leavesPending.slice(0, 10).map(l => (
              <li key={l.leave_id} className="px-5 py-3 text-[13px] flex items-center gap-3">
                <span className="flex-1 truncate">{l.user_name || l.user_id} · {l.leave_type}</span>
                <span className="font-mono text-[11px] text-[var(--span-text-2)]">{l.start_date} → {l.end_date}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ------------------ Consulting ------------------
function ConsultingHeadDashboard({ user }) {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => {
    try {
      const [cs, sg] = await Promise.all([
        api.get("/cases").catch(() => ({ data: [] })),
        api.get("/sighting-reports").catch(() => ({ data: [] })),
      ]);
      const active = (cs.data || []).filter(c => c.status !== "closed");
      setData({
        activeCases: active.length,
        recentCases: active.slice(0, 10),
        sightings: (sg.data || []).length,
        recentSightings: (sg.data || []).slice(0, 10),
      });
    } catch (e) { setData({}); }
  })(); }, []);

  return (
    <div className="space-y-6" data-testid="dept-head-dashboard-consulting">
      <Header user={user} title="Consulting · Department Dashboard" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Tile label="Active cases" value={data?.activeCases ?? "—"} testid="dh-cons-active-cases" />
        <Tile label="Sighting reports" value={data?.sightings ?? "—"} sub="All time" testid="dh-cons-sightings" />
        <Tile label="Analytics" value="View →" sub="Operational dashboards" testid="dh-cons-analytics" link="/analytics" />
      </div>
      <Pair
        leftTitle="Active cases" leftLink="/cases" leftTestid="dh-cons-case-list"
        leftItems={(data?.recentCases || []).map(c => ({ key: c.case_id, text: `${c.case_code} · ${(c.title || "").slice(0, 50)}` }))}
        rightTitle="Recent sightings" rightLink="/reports/sighting" rightTestid="dh-cons-sighting-list"
        rightItems={(data?.recentSightings || []).map(s => ({ key: s.report_id, text: `${s.title || s.summary?.slice(0, 50) || "Sighting"} · ${s.status}` }))}
      />
    </div>
  );
}

function GenericHeadDashboard({ user }) {
  return (
    <div className="space-y-6" data-testid="dept-head-dashboard-generic">
      <Header user={user} title={`${user?.department} · Department Dashboard`} />
      <div className="card-flat p-6 text-center text-[12px] text-[var(--span-text-2)]">
        Department-specific dashboard coming soon. <Link to="/analytics" className="underline">Open analytics →</Link>
      </div>
    </div>
  );
}

function ReceivablesAgingTile({ aging }) {
  const buckets = aging?.buckets || {};
  const order = ["0-30", "31-60", "61-90", "90+"];
  const max = Math.max(1, ...order.map(k => buckets[k]?.amount_inr || 0));
  return (
    <div className="card-flat" data-testid="receivables-aging-tile">
      <div className="px-5 py-3 border-b border-[var(--span-border)] flex items-end justify-between">
        <div>
          <div className="label-eyebrow">Receivables Aging</div>
          <div className="font-display font-bold text-[22px] tnum mt-1 leading-none">
            {fmtMoney(aging?.total_outstanding_inr || 0, "INR")}
          </div>
          <div className="text-[11px] text-[var(--span-text-2)] mt-1 font-mono">
            {aging?.total_invoices || 0} unpaid invoices · as of {(aging?.as_of || "").slice(0, 10)}
          </div>
        </div>
        <Link to="/invoices" className="text-[11px] font-mono text-[var(--span-text-2)] uppercase tracking-[0.12em] hover:text-[var(--span-text)]">Go to invoices →</Link>
      </div>
      <div className="grid grid-cols-4 gap-0 divide-x divide-[var(--span-border)]">
        {order.map(k => {
          const b = buckets[k] || { count: 0, amount_inr: 0 };
          const pct = (b.amount_inr / max) * 100;
          const tone = k === "0-30" ? "var(--span-success, #12B76A)"
            : k === "31-60" ? "var(--span-warn, #F79009)"
            : k === "61-90" ? "var(--span-warn, #F79009)"
            : "var(--span-danger, #D92D20)";
          return (
            <div key={k} className="p-4" data-testid={`aging-bucket-${k}`}>
              <div className="label-eyebrow text-[10px]">{k} days</div>
              <div className="font-display font-bold tnum mt-1 text-[18px]">
                {fmtMoney(b.amount_inr, "INR")}
              </div>
              <div className="text-[10px] font-mono text-[var(--span-text-2)] mt-0.5">{b.count} invoices</div>
              <div className="h-1 mt-3 bg-[var(--span-bg-2)]">
                <div className="h-full" style={{ width: `${pct}%`, background: tone }} />
              </div>
            </div>
          );
        })}
      </div>
      {aging?.oldest_invoices?.length > 0 && (
        <div className="border-t border-[var(--span-border)]">
          <div className="px-5 py-2 label-eyebrow">Oldest 5 unpaid</div>
          <ul className="divide-y divide-[var(--span-border)]">
            {aging.oldest_invoices.slice(0, 5).map((i, idx) => (
              <li key={i.invoice_id || idx} className="px-5 py-2 flex items-center gap-3 text-[12px]">
                <span className="font-mono w-[100px] shrink-0">{i.invoice_number || i.invoice_id?.slice(0, 12)}</span>
                <span className="font-mono text-[var(--span-text-2)] w-[80px] shrink-0">{i.age_days}d old</span>
                <span className="flex-1 tnum text-right font-mono">{fmtMoney(i.amount_inr, "INR")}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function GenericHeadDashboardOriginal_REMOVED() { return null; }

// ------------------ shared atoms ------------------
function Header({ user, title }) {
  return (
    <div>
      <div className="label-eyebrow">Department Head · Tier 3</div>
      <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">{title}</h1>
      <div className="text-[12px] text-[var(--span-text-2)] mt-1 font-mono">Welcome back, {user?.name}.</div>
    </div>
  );
}

function Tile({ label, value, sub, link, testid }) {
  const content = (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="label-eyebrow">{label}</div>
      <div className="font-display font-bold mt-1 tnum text-[28px] leading-none">{value}</div>
      {sub && <div className="text-[11px] text-[var(--span-text-2)] mt-2 font-mono">{sub}</div>}
    </div>
  );
  return link ? <Link to={link}>{content}</Link> : content;
}

function Pair({ leftTitle, leftLink, leftItems, leftTestid, rightTitle, rightLink, rightItems, rightTestid }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <ListBlock title={leftTitle} link={leftLink} items={leftItems} testid={leftTestid} />
      <ListBlock title={rightTitle} link={rightLink} items={rightItems} testid={rightTestid} />
    </div>
  );
}

function ListBlock({ title, link, items, testid }) {
  return (
    <div className="card-flat" data-testid={testid}>
      <div className="px-5 py-3 border-b border-[var(--span-border)] flex items-center justify-between">
        <div className="label-eyebrow">{title}</div>
        <Link to={link} className="text-[11px] font-mono text-[var(--span-text-2)] uppercase tracking-[0.12em] hover:text-[var(--span-text)]">View →</Link>
      </div>
      {items.length === 0 ? (
        <div className="px-5 py-6 text-center text-[12px] text-[var(--span-text-2)]">No data.</div>
      ) : (
        <ul className="divide-y divide-[var(--span-border)]">
          {items.slice(0, 10).map((x, idx) => (
            <li key={x.key || idx} className="px-5 py-3 text-[13px] truncate">{x.text}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
